import { SUBSCRIPTION_COMPONENT_TRANSLATIONS as T } from "@/components/subscription/constants";
import { subscriptionPixCheckoutEnabled } from "@/domains/subscription/billingHelpers";
import { Button } from "@/domains/shared/components/Button";
import { isPaidPlan, isTrialPlan, normalizePlanSlug, type SubscriptionPlanId } from "@/domains/subscription/plans";
import { usePlanCatalog } from "@/domains/subscription/usePlanCatalog";
import { getTranslation } from "@mivo/i18n";

type TrialPlanUpgradeConfirmStepProps = {
    currentPlanId: SubscriptionPlanId;
    targetPlanId: SubscriptionPlanId;
    onCancel: () => void;
    onConfirm: () => void;
};

export function TrialPlanUpgradeConfirmStep({
    currentPlanId,
    targetPlanId,
    onCancel,
    onConfirm,
}: TrialPlanUpgradeConfirmStepProps) {
    const { getPlan } = usePlanCatalog();
    const currentPlan = getPlan(currentPlanId);
    const targetPlan = getPlan(targetPlanId);
    const paymentMethods = subscriptionPixCheckoutEnabled
        ? getTranslation(T.trialUpgradePaymentPixOrCard)
        : getTranslation(T.trialUpgradePaymentCardOnly);

    return (
        <div className="space-y-4 text-sm text-muted-foreground" data-testid="checkout-trial-upgrade-confirm">
            <p>
                {getTranslation(T.trialUpgradeIntro)
                    .replace("{currentPlan}", currentPlan.name)
                    .replace("{targetPlan}", targetPlan.name)
                    .replace("{price}", targetPlan.priceLabel)
                    .replace("{period}", targetPlan.periodLabel)
                    .replace("{paymentMethods}", paymentMethods)}
            </p>
            <p>{getTranslation(T.trialUpgradeStayOnBasic)}</p>
            <div className="flex flex-row gap-2">
                <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    data-testid="checkout-trial-upgrade-cancel"
                    onClick={onCancel}
                >
                    {getTranslation(T.cancel)}
                </Button>
                <Button type="button" className="flex-1" data-testid="checkout-trial-upgrade-agree" onClick={onConfirm}>
                    {getTranslation(T.agreeAndPay)}
                </Button>
            </div>
        </div>
    );
}

export function requiresTrialUpgradeConfirmation(
    currentPlanId: string | null | undefined,
    targetPlanId: string | null | undefined,
    hasActivePaidSubscription: boolean,
): boolean {
    if (hasActivePaidSubscription) {
        return false;
    }

    const currentSlug = normalizePlanSlug(currentPlanId);
    const targetSlug = normalizePlanSlug(targetPlanId);
    return isTrialPlan(currentSlug) && isPaidPlan(targetSlug) && currentSlug !== targetSlug;
}

/** Trial-only slug check; prefer {@link requiresTrialUpgradeConfirmation} at call sites with billing context. */
export function isUpgradingFromBasicTrial(
    currentPlanId: string | null | undefined,
    targetPlanId: string | null | undefined,
): boolean {
    return requiresTrialUpgradeConfirmation(currentPlanId, targetPlanId, false);
}
